import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import DOMPurify from "dompurify";

import Translator, { __ } from "services/Translator";
import RaceCountdown from "./RaceCountdown";
import {
  findUserLeaderboardEntry,
  formatPoints,
  getInitials,
  getLeaderboardDisplayName,
  getMedalClass,
  getPrizeForRank,
  getPrizeTable,
  getRaceEndDate,
  getRankDelta,
  getRowClassName,
  isCurrentPlayerRow,
  isPrizeTableFallback,
} from "./weeklyRaceUtils";

const HERO_IMG = require("assets/play365/images/features/feature-banner-one.webp");
const COIN_BTC = require("assets/play365/images/weekly-race/coin-btc.webp");
const COIN_ETH = require("assets/play365/images/weekly-race/coin-eth.webp");
const COIN_SOL = require("assets/play365/images/weekly-race/coin-sol.webp");
const COIN_XRP = require("assets/play365/images/weekly-race/coin-xrp.webp");

// Designed brand watermark — an intentional clover mark, not random glyph
// confetti. Rendered at low opacity behind the leaderboard.
function CloverGlyph({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <g fill="currentColor">
        <ellipse cx="12" cy="7.2" rx="3.5" ry="4.3" />
        <ellipse cx="16.8" cy="12" rx="4.3" ry="3.5" />
        <ellipse cx="12" cy="16.8" rx="3.5" ry="4.3" />
        <ellipse cx="7.2" cy="12" rx="4.3" ry="3.5" />
        <rect x="11.3" y="14" width="1.4" height="7.5" rx="0.7" />
      </g>
    </svg>
  );
}

// Inline SVG marks — platform-independent, no external asset needed.
function TrophyGlyph({ size = 18, className = "trophy-svg" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M6 3h12v2h3v3a4 4 0 0 1-4 4h-.6A5 5 0 0 1 13 15.9V18h3v2H8v-2h3v-2.1A5 5 0 0 1 7.6 12H7a4 4 0 0 1-4-4V5h3V3Zm0 4H5v1a2 2 0 0 0 1 1.7V7Zm12 0v2.7A2 2 0 0 0 19 8V7h-1Z"
      />
    </svg>
  );
}

function MedalMark({ rank }) {
  if (rank === 1) return <TrophyGlyph size={18} className="medal-svg" />;
  if (rank === 2 || rank === 3) {
    return (
      <svg
        className="medal-svg"
        viewBox="0 0 24 24"
        width={16}
        height={16}
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="m12 2 2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17l-5.8 3 1.1-6.5L2.6 8.8l6.5-.9L12 2Z"
        />
      </svg>
    );
  }
  return rank;
}

function PodiumGlyph() {
  return (
    <svg
      className="podium-svg"
      viewBox="0 0 64 40"
      width={96}
      height={60}
      aria-hidden="true"
      focusable="false"
    >
      <rect x="4" y="20" width="16" height="18" rx="2" fill="currentColor" opacity=".55" />
      <rect x="24" y="10" width="16" height="28" rx="2" fill="currentColor" />
      <rect x="44" y="24" width="16" height="14" rx="2" fill="currentColor" opacity=".4" />
    </svg>
  );
}

function RankDelta({ row }) {
  const { type, value } = getRankDelta(row);
  if (type === "up") {
    return (
      <span className="delta up">
        <i>▲</i>
        {value}
      </span>
    );
  }
  if (type === "down") {
    return (
      <span className="delta down">
        <i>▼</i>
        {value}
      </span>
    );
  }
  return (
    <span className="delta same">
      <i>—</i>
    </span>
  );
}

function LeaderboardRow({ row, rank, userDetails, race = null, forceMe = false, gapToQualify = null }) {
  const isMe = forceMe || isCurrentPlayerRow(row, userDetails);
  const name = getLeaderboardDisplayName(row);

  return (
    <div className={getRowClassName(rank, isMe)} key={`${name}-${rank}`}>
      <span className="rk-cell">
        <span className={getMedalClass(rank)}>
          <MedalMark rank={rank} />
        </span>
        <RankDelta row={row} />
      </span>
      <span className="pl">
        <span className="av">{getInitials(name)}</span>
        <span className="nm">
          {name}
          {isMe ? (
            <span className="you-badge"> {__("You")}</span>
          ) : null}
        </span>
      </span>
      <span className="pz">
        {gapToQualify != null ? (
          <span className="gap-pill">
            ▲ {formatPoints(gapToQualify)} {__("pts")}
          </span>
        ) : (
          getPrizeForRank(rank, row, race)
        )}
      </span>
      <span className="mt">{formatPoints(row.wagered)} {__("pts")}</span>
    </div>
  );
}

function RankUpToast({ toast, onDismiss }) {
  if (!toast) return null;
  return (
    <div
      className={`toast${toast.dismissing ? " dismissing" : ""}`}
      role="status"
    >
      <div className="toast-icon" aria-hidden="true">
        <TrophyGlyph size={26} className="toast-trophy-svg" />
      </div>
      <div>
        <div className="toast-title">{toast.title}</div>
        <div className="toast-msg">{toast.msg}</div>
      </div>
      <button
        type="button"
        className="toast-x"
        aria-label={__("Dismiss")}
        onClick={onDismiss}
      >
        ×
      </button>
    </div>
  );
}

export default function WeeklyRaceContent({
  race,
  leaderboard = [],
  qualifyingGames = [],
  otherRaces = [],
  pastRaces = [],
  userDetails,
  isAuthenticated,
  isPreviewDemo = false,
  demoNotQualified = null,
  onRankImprovement,
}) {
  const langCode = Translator.getSelectedLanguage().code;
  const endAt = getRaceEndDate(race);
  const topTen = leaderboard.slice(0, 10);
  const userEntry = findUserLeaderboardEntry(leaderboard, userDetails);
  const showNotQualified =
    (isPreviewDemo && demoNotQualified) ||
    (isAuthenticated && userEntry && userEntry.rank > 10);
  const tenthPlace = topTen[9];
  const previewRank = demoNotQualified?.rank ?? userEntry?.rank;
  const previewPoints =
    demoNotQualified?.points ?? userEntry?.row?.wagered ?? 0;
  const pointsGap =
    showNotQualified && (tenthPlace || demoNotQualified)
      ? demoNotQualified?.gap ??
        Math.max(0, Number(tenthPlace.wagered) - Number(userEntry.row.wagered))
      : 0;

  const showUserAnchor =
    isAuthenticated && userEntry && userEntry.rank > 10;
  const anchorHiddenCount = userEntry ? userEntry.rank - 11 : 0;

  // Only ever present the prize pool when the race payload actually supplies
  // it — never fabricate a number for a real-money promotion.
  const realPrizePool =
    race?.prize_pool_fs || race?.free_spins_pool || race?.total_free_spins || null;
  const hasRealPool = realPrizePool != null;
  const prizeTableIsExample = isPrizeTableFallback(race);
  const activeRacers =
    race?.participants_count || race?.player_count || leaderboard.length;

  const [heroFailed, setHeroFailed] = useState(false);
  const [toast, setToast] = useState(null);
  const prevRankRef = useRef(null);

  const playHref = race?.id
    ? `/games/video_slots?race=${race.id}`
    : "/games/video_slots";

  useEffect(() => {
    if (isPreviewDemo || !isAuthenticated || !userEntry) {
      prevRankRef.current = userEntry?.rank ?? null;
      return;
    }
    const prev = prevRankRef.current;
    if (prev != null && userEntry.rank < prev) {
      const improved = prev - userEntry.rank;
      const payload = {
        title: `${__("You moved up")} ${improved} ${__("places!")}`,
        msg: __("Keep playing qualifying games to climb the leaderboard."),
      };
      setToast(payload);
      onRankImprovement?.(payload);
    }
    prevRankRef.current = userEntry.rank;
  }, [userEntry?.rank, isAuthenticated, onRankImprovement, userEntry]);

  const dismissToast = () => {
    setToast((t) => (t ? { ...t, dismissing: true } : null));
    window.setTimeout(() => setToast(null), 350);
  };

  useEffect(() => {
    if (!toast || toast.dismissing) return undefined;
    const id = window.setTimeout(dismissToast, 5500);
    return () => window.clearTimeout(id);
  }, [toast]);

  const raceTitle =
    race?.lang?.title || race?.title || __("Weekly Clover Race");
  const raceSubtitle =
    race?.lang?.campaign_information ||
    __("Most points wagered this week win!");

  return (
    <div className="weekly-race">
      <div className="weekly-race__page">
        {isPreviewDemo ? (
          <p className="weekly-race__preview-note" role="status">
            {__(
              "Preview mode — showing sample data until the race API returns live results."
            )}
          </p>
        ) : null}
        <nav className="crumb" aria-label={__("Breadcrumb")}>
          <Link hreflang={langCode} to="/">
            {__("Home")}
          </Link>
          <span className="sep">›</span>
          <Link hreflang={langCode} to="/promotions">
            {__("Promotions")}
          </Link>
          <span className="sep">›</span>
          <b>{raceTitle}</b>
        </nav>

        <section className="panel bg-hero race-hero">
          <div className="race-copy">
            <span className="eyebrow">
              <span className="dot-live" aria-hidden="true" />
              {__("Live tournament")}
            </span>
            <h1>
              <span className="gold">{raceTitle}</span>
              <span className="wht">{raceSubtitle}</span>
            </h1>
            <p className="sub">
              {__(
                "Earn 1 point for every $1 wagered on qualifying slots and crash games. The top 10 players on the leaderboard split the gold."
              )}
            </p>
            <div className="race-stats">
              <div>
                <b>{hasRealPool ? `${realPrizePool} FS` : __("Free Spins")}</b>
                {__("prize pool")}
              </div>
              <div>
                <b>{__("Top 10")}</b>
                {__("get paid")}
              </div>
              <div>
                <b>$1 = 1 pt</b>
                {__("wagered")}
              </div>
            </div>
          </div>
          <div className="race-art-wrap">
            {!heroFailed ? (
              <img
                className="race-art-img"
                src={race?.race_banner || HERO_IMG}
                alt=""
                loading="eager"
                decoding="async"
                onError={() => setHeroFailed(true)}
              />
            ) : null}
          </div>
        </section>

        <div className="cd-wrap">
          <RaceCountdown endAt={endAt} />
          <section className="panel bg-pool pool">
            <img className="coin eth" src={COIN_ETH} alt="" loading="lazy" />
            <img className="coin sol" src={COIN_SOL} alt="" loading="lazy" />
            <img className="coin xrp" src={COIN_XRP} alt="" loading="lazy" />
            <img className="coin btc" src={COIN_BTC} alt="" loading="lazy" />
            <div>
              <div className="cd-lbl">{__("Total prize pool")}</div>
              <div className="v">
                {hasRealPool ? (
                  <>
                    {realPrizePool}
                    <span>{__("Free Spins")}</span>
                  </>
                ) : (
                  <>
                    {__("Top 10")}
                    <span>{__("share the pool")}</span>
                  </>
                )}
              </div>
              <div className="d">
                {hasRealPool
                  ? __("Split across the top 10 racers")
                  : __("Prize pool announced at race start")}
              </div>
            </div>
            <div className="meta">
              <div>
                <b>{formatPoints(activeRacers)}</b>
                {__("Active racers")}
              </div>
              <div>
                <b>{__("Top 10")}</b>
                {__("Paid out")}
              </div>
              <div>
                <b>{__("48 hrs")}</b>
                {__("Credit time")}
              </div>
            </div>
          </section>
        </div>

        <div className="cta-strip bg-cta panel">
          <span>
            <i aria-hidden="true">●</i> {__("Play now to climb the leaderboard")}
          </span>
          <Link
            hreflang={langCode}
            to={playHref}
            className="btn btn-primary btn-lg"
          >
            {__("Play qualifying games")}
          </Link>
        </div>

        <div className="section-title">
          <h2>{__("Leaderboard")}</h2>
          <small>
            {__(
              "updates live · ranked by points · top 10 win · ▲▼ vs yesterday"
            )}
          </small>
        </div>
        <section className="panel bg-leader lb-card">
          <span className="watermark" aria-hidden="true">
            <CloverGlyph />
          </span>
          <div className="lb-head">
            <span>{__("Rank")}</span>
            <span>{__("Player")}</span>
            <span style={{ textAlign: "right" }}>{__("Prize")}</span>
            <span style={{ textAlign: "right" }}>{__("Points")}</span>
          </div>
          {topTen.length ? (
            topTen.map((row, index) => (
              <LeaderboardRow
                key={row.player_login || row.player_anonymous_tmp_id || index}
                row={row}
                rank={index + 1}
                userDetails={userDetails}
                race={race}
              />
            ))
          ) : (
            <div className="lb-empty" role="status">
              <PodiumGlyph />
              <p className="lb-empty__title">
                {__("The podium is empty — your name could be first.")}
              </p>
              <Link
                hreflang={langCode}
                to={playHref}
                className="btn btn-primary"
              >
                {__("Play qualifying games")}
              </Link>
            </div>
          )}

          {showUserAnchor ? (
            <>
              {anchorHiddenCount > 0 ? (
                <div className="lb-gap" role="separator">
                  {`… ${anchorHiddenCount} ${__("more players")} …`}
                </div>
              ) : null}
              <LeaderboardRow
                row={userEntry.row}
                rank={userEntry.rank}
                userDetails={userDetails}
                race={race}
                forceMe
                gapToQualify={pointsGap}
              />
            </>
          ) : null}

          {leaderboard.length > 10 ? (
            <div className="lb-note" aria-hidden="true">
              {__(
                "Positions 11 and below don't share the prize pool — keep climbing!"
              )}
            </div>
          ) : null}
        </section>

        {showNotQualified ? (
          <section className="panel bg-notyet notyet-card">
            <div className="ny-grid">
              <div className="ny-rank">
                <span className="ny-lbl">{__("Your rank")}</span>
                <span className="ny-num">#{previewRank}</span>
                <span className="ny-distance">
                  ▲ {Math.max(1, previewRank - 10)} {__("to qualify")}
                </span>
              </div>
              <div>
                <div className="ny-title">
                  {__("You're not yet in the prize zone")}
                </div>
                <p className="ny-msg">
                  {__("You've earned")}{" "}
                  <b>{formatPoints(previewPoints)}</b>{" "}
                  {__("points so far this week. You need")}{" "}
                  <b>{formatPoints(pointsGap)}</b>{" "}
                  {__(
                    "more points ($1 = 1 pt) to break into the top 10 and start winning free spins."
                  )}
                </p>
              </div>
              <div className="ny-cta">
                <Link
                  hreflang={langCode}
                  to={playHref}
                  className="btn btn-primary btn-lg"
                >
                  {__("Play qualifying games")}
                </Link>
                <small>{__("or pick a game from the list below ↓")}</small>
              </div>
            </div>
          </section>
        ) : null}

        {qualifyingGames.length ? (
          <>
            <div className="section-title">
              <h2>{__("Qualifying games")}</h2>
              <small>{__("any spin on these counts")}</small>
              <Link hreflang={langCode} to={playHref} className="more">
                {__("All games ›")}
              </Link>
            </div>
            <section id="qualifying-games" className="panel bg-qual qual-wrap">
              <div className="qual">
                {qualifyingGames.slice(0, 6).map((game, index) => (
                  <Link
                    key={game.id || index}
                    hreflang={langCode}
                    to={game.slug ? `/play/${game.slug}` : playHref}
                    className="qt"
                    style={
                      game.game_snapshot_url
                        ? {
                            backgroundImage: `linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.75)), url(${game.game_snapshot_url})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }
                        : {
                            background:
                              game.tileStyle ||
                              "linear-gradient(160deg,#1d6e3a,#0c3a1e)",
                          }
                    }
                  >
                    {game.badge || index < 2 ? (
                      <span className="qb">
                        {game.badge || __("Hot")}
                      </span>
                    ) : null}
                    <span className="lbl">{game.name || game.game_name}</span>
                  </Link>
                ))}
              </div>
            </section>
          </>
        ) : null}

        <div className="section-title">
          <h2>{__("Race rules & prizes")}</h2>
          <small>{__("read before claiming")}</small>
        </div>
        <details className="panel bg-terms acc">
          <summary>{__("Terms & conditions")}</summary>
          <div className="body">
            {race?.lang?.description ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(race.lang.description),
                }}
              />
            ) : (
              <>
                <h3>{__("1 · How to win")}</h3>
                <ul>
                  <li>{__("Play any qualifying slot or crash game to earn points.")}</li>
                  <li>
                    <b>{__("Every $1 wagered = 1 point.")}</b>{" "}
                    {__(
                      "Points accumulate across all qualifying games during the promotional period."
                    )}
                  </li>
                  <li>
                    {__(
                      "The 10 players with the most points at the end of the week split the prize pool."
                    )}
                  </li>
                </ul>
                <h3>{__("2 · Game rules")}</h3>
                <ul>
                  <li>
                    <b>{__("1 point per $1 wagered")}</b>{" "}
                    {__(
                      "(in your account currency, converted to USD for scoring)."
                    )}
                  </li>
                  <li>
                    {__(
                      "Points are calculated on real-money wagers only — bonus-funds wagers do not count."
                    )}
                  </li>
                  <li>
                    {__(
                      "Promotion runs weekly, Monday 00:00 UTC → Sunday 23:59 UTC."
                    )}
                  </li>
                  <li>
                    {__(
                      "In a tie, the player who reached the points total first ranks higher."
                    )}
                  </li>
                </ul>
              </>
            )}
            <h3>{__("3 · Race prizes")}</h3>
            {prizeTableIsExample ? (
              <p className="prize-tba">
                {__(
                  "Prizes are announced at the start of each race and shared across the top 10 finishers."
                )}
              </p>
            ) : (
              <div className="prize-table">
                {getPrizeTable(race).map((tier) => (
                  <div
                    key={tier.label}
                    className={`pt${tier.first ? " first" : ""}`}
                  >
                    <div className="r">{tier.label}</div>
                    <div className="p">
                      {tier.fs} {__("FS")}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <h3>{__("4 · Free-spin terms")}</h3>
            <ul>
              <li>
                {__(
                  "Free spins must be used within 5 days of being credited or they expire."
                )}
              </li>
              <li>
                {__(
                  "Rewards are credited to your account within 48 hours of race completion."
                )}
              </li>
            </ul>
          </div>
        </details>

        {otherRaces.length ? (
          <>
            <div className="section-title">
              <h2>{__("Other ongoing races")}</h2>
            </div>
            {otherRaces.map((item) => (
              <section className="panel bg-row ro" key={item.id}>
                <div className="lf">
                  <div className="ic" aria-hidden="true">
                    <CloverGlyph />
                  </div>
                  <div>
                    <div className="t">
                      {item.lang?.title || item.title}
                    </div>
                    <div className="s">
                      {item.lang?.campaign_information || ""}
                    </div>
                  </div>
                </div>
                <div className="rt">
                  <div className="v">
                    {item.prize_pool || item.prize_pool_fs || __("Prizes")}
                  </div>
                  <div className="c">{__("Weekly")}</div>
                </div>
              </section>
            ))}
          </>
        ) : null}

        {pastRaces.length ? (
          <>
            <div className="section-title">
              <h2>{__("Past races")}</h2>
              <small>{__("last 30 days")}</small>
            </div>
            <div className="past-list">
              {pastRaces.map((item) => (
                <div className="past" key={item.id}>
                  <div className="lf">
                    <span className="tro" aria-hidden="true">
                      <TrophyGlyph size={16} className="past-trophy-svg" />
                    </span>
                    {item.lang?.title || item.title || raceTitle}
                    <span className="dt">
                      {item.expiry_date
                        ? moment(item.expiry_date).format("MMM D, YYYY")
                        : ""}
                    </span>
                  </div>
                  <div className="wn">
                    {item.winner_name || __("Winner")}
                    <span className="pz">
                      {item.winner_prize || getPrizeForRank(1, item)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>

      <RankUpToast toast={toast} onDismiss={dismissToast} />
    </div>
  );
}
